/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: user_mentions entity configuration
 * Generator: scripts/generate-api-clients/generate-api-clients.ts
 *
 * This file contains the API client for the user_mentions table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the user_mentions API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, note_id, user_profile_id, mentioned_at, mentioned_by, mention_type, is_read, read_at, trig_user_full_name, trig_user_email, trig_user_phone_number, trig_note_content, trig_note_title]
 * Searchable fields: []
 * Filterable fields: [id, note_id, user_profile_id, is_read]
 */

import { httpClient } from '../http-client.js';
import { globalSearchConfig } from '../../../config/search/global-search-config.js';
import { matchesAllTerms } from '../../../config/search/search-utils.js';
import type {
  UserMentionsRow,
  UserMentionsInsert,
  UserMentionsUpdate
} from '../generated_schemas/generated-user_mentions-schema.js';
import { UserMentionsQueryOptions } from '../generated_schemas/generated-user_mentions-schema.js';
export type { UserMentionsQueryOptions };

// Internal response type with non-null data (not exported to avoid conflicts with schema's Response type)
interface UserMentionsClientResponse {
  data: UserMentionsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for user_mentions table operations
 *
 * @description Provides type-safe access to user_mentions API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class UserMentionsClient {
  private readonly basePath = '/user-mentions';

  /**
   * Get all user_mentions records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of user_mentions records
   */
  async getAll(options?: UserMentionsQueryOptions): Promise<UserMentionsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<UserMentionsClientResponse>(url);
    return response.data;
  }

  /**
   * Get a single user_mentions record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the user_mentions record
   */
  async getById(id: string): Promise<UserMentionsRow> {
    const response = await httpClient.get<{
      data: UserMentionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new user_mentions record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created user_mentions record
   */
  async create(data: UserMentionsInsert): Promise<UserMentionsRow> {
    const response = await httpClient.post<{
      data: UserMentionsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing user_mentions record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated user_mentions record
   */
  async update(id: string, data: UserMentionsUpdate): Promise<UserMentionsRow> {
    const response = await httpClient.put<{
      data: UserMentionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a user_mentions record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple user_mentions records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created user_mentions records
   */
  async createMany(data: UserMentionsInsert[]): Promise<UserMentionsRow[]> {
    const response = await httpClient.post<{
      data: UserMentionsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple user_mentions records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search user_mentions with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    query: string,
    columns?: string[],
    options?: UserMentionsQueryOptions
  ): Promise<UserMentionsClientResponse> {
    throw new Error('Search operations are not available for user_mentions - no searchable fields configured');
  }

  /**
   * Get count of user_mentions records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a user_mentions record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'user_mentions',
      publicFields: ['id', 'note_id', 'user_profile_id', 'mentioned_at', 'mentioned_by', 'mention_type', 'is_read', 'read_at', 'trig_user_full_name', 'trig_user_email', 'trig_user_phone_number', 'trig_note_content', 'trig_note_title'],
      searchableFields: [],
      filterableFields: ['id', 'note_id', 'user_profile_id', 'is_read'],
      sortableFields: [],
      readonlyFields: ['id', 'trig_user_full_name', 'trig_user_email', 'trig_user_phone_number', 'trig_note_content', 'trig_note_title'],
      requiredFields: ['note_id', 'user_profile_id'],
    };
  }
}

// Export singleton instance for convenience
export const userMentionsClient = new UserMentionsClient();
